/**
 * Which Cashflow & Age Pension option wins, for each of the three things a client might want.
 *
 * SHARED, not duplicated. The server evaluates the candidates and the frontend renders them, and
 * both need to agree on which one is "best cashflow". Two copies of this rule would be two answers
 * to one question, free to drift — the defect shape this codebase keeps meeting. It lives here so
 * there is one.
 *
 * Pure arithmetic over results that have already been projected; no engine dependencies, which is
 * why it can sit in the shared slice at all.
 */

/** The three things a client might be optimising for. */
export type CashflowPensionGoal = 'cashflow' | 'pension' | 'estate';

export interface RankableResult {
    id: string;
    changeNothing?: boolean;
    error?: string;
    snapshot: {
        pension: number;
        cashflow: number;
        estate: number;
        shortfallYear?: number | null;
    };
}

/**
 * The best candidate for each goal — or `null` where none beats leaving things alone.
 *
 * RETURNING NULL IS THE POINT. An engine that always crowns a winner will eventually recommend an
 * action that costs the client money, because it has no way to say "the best thing you can do here
 * is nothing". Doing nothing is a real answer and has to be expressible.
 *
 * The margin is deliberately more than zero: a candidate that improves a figure by a few dollars a
 * year is noise, and presenting it as a recommendation invites a client to commit capital for life
 * in exchange for nothing.
 */
export function bestForEachGoal<T extends RankableResult>(
    results: T[],
    minimumWorthDoing = 100,
): Record<CashflowPensionGoal, T | null> {
    const baseline = results.find((r) => r.changeNothing && !r.error);
    const usable = results.filter((r) => !r.changeNothing && !r.error);

    const pick = (goal: CashflowPensionGoal): T | null => {
        // A tie (within a dollar) goes to the plan that does better on the other goals, the estate first: two plans
        // with the same Age Pension, one also paying off the credit card and leaving more, led by list order
        // (owner's file, 22 Oct 2026).
        const others = (['estate', 'cashflow', 'pension'] as CashflowPensionGoal[]).filter((g) => g !== goal);
        const ranked = [...usable].sort((a, b) => {
            const d = b.snapshot[goal] - a.snapshot[goal];
            if (Math.abs(d) >= 1) return d;
            for (const g of others) { const e = b.snapshot[g] - a.snapshot[g]; if (Math.abs(e) >= 1) return e; }
            return 0;
        });
        const top = ranked[0];
        if (!top) return null;
        if (!baseline) return top;
        return top.snapshot[goal] - baseline.snapshot[goal] > minimumWorthDoing ? top : null;
    };

    return { cashflow: pick('cashflow'), pension: pick('pension'), estate: pick('estate') };
}
